import { X } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { DeleteIcon } from '@/components/icons'
import { useDeleteTestCase } from '@/hooks'
import type { TestCase } from '@/types'

interface DeleteTestCaseDialogProps {
  projectId: string
  testCase: TestCase
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteTestCaseDialog({ projectId, testCase, open, onOpenChange }: DeleteTestCaseDialogProps) {
  const deleteTestCase = useDeleteTestCase(projectId)

  const handleDelete = async () => {
    try {
      await deleteTestCase.mutateAsync(testCase.id)
      toast.success(`${testCase.testCaseIdentifier} deleted`)
      onOpenChange(false)
    } catch {
      toast.error('Failed to delete test case')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-lg" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-primary">Delete Test Case?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-primary">
          Are you sure you want to delete the test case "<span className="font-bold">{testCase.testCaseIdentifier} &mdash; {testCase.title}</span>"?
          This action cannot be undone. The test case will be permanently removed from the list, and any linked work items will be unlinked.
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button
              variant="ghost"
              disabled={deleteTestCase.isPending}
              className="h-10 px-4 font-bold"
              data-testid={`cancel-delete-test-case-${testCase.testCaseIdentifier}`}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteTestCase.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
            data-testid={`confirm-delete-test-case-${testCase.testCaseIdentifier}`}
          >
            <DeleteIcon className="h-6 w-6" />
            {deleteTestCase.isPending ? 'Deleting...' : 'Delete Test Case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
